import React, { useState, useEffect } from 'react';
import logger from "../utils/logger";

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Form, Button } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faFileEdit, faDownload, faAnglesLeft, faAnglesRight, faBuilding, faPlus, faAddressBook } from '@fortawesome/free-solid-svg-icons';


import { useTheme } from './themeControl/ThemeProvider';
import { useAlert } from '../providers/AlertProvider';
import { useAuthProvider } from '../providers/AuthProvider';

import { ActivityIndicator } from '../components/custom/ActivityIndicator';

import { validSession } from '../components/lib/validSession';
import CustomerEditModal from '../components/custom/CustomerEditModal';
import CustomerAddRouteModal from '../components/custom/CustomerAddRouteModal';
import AddressBookPanel from '../components/custom/AddressBookPanel';

import { sortList, downloadCSV } from './lib/functions';

// Api
import { fetch_all_customers, update_customer, add_new_customer, fetch_customer_deliveries, add_customer_delivery, edit_customer_delivery, delete_customer_delivery, fetch_address_book, add_address_book_entry, update_address_book_entry, delete_address_book_entry, fetch_pallet_network_costs, save_pallet_network_costs, delete_pallet_network_costs } from '../api/customers';
import { fetch_all_warehouse_costs, add_warehouse_costs, edit_warehouse_costs, delete_warehouse_costs } from '../api/warehouse';

// import css
import './css/GlobalStyles.css';
import './css/DriversStyles.css';
import './css/LeftNavigation.css';
import './css/Styles.css';

// import images
import './img/light-driver.png';
import './img/dark-driver.png';
import './img/light-admin.png';
import './img/dark-admin.png';
import './img/light-service.png';
import './img/dark-service.png';
import './img/edit.png';
import './img/route.png';
import './img/unlock.png';

const CustomerPage = () => {
    const { theme } = useTheme();
    const { showAlert } = useAlert();
    const { logout, isAdmin, isManager, isDev, features, permissions } = useAuthProvider();
    const canEdit = isDev || permissions.perm_customers_edit;

    const navigate  = useNavigate();

    // Images
    const edit = require(`./img/edit.png`);
    const route = require(`./img/route.png`);

    // ==========================================================================================================================
    // ========================================================[States]==========================================================
    // ==========================================================================================================================

    // loading state
    const [isLoading, setIsLoading] = useState(false);

    // State to manage active tab
    const [activeTab, setActiveTab] = useState('customers');
    const [showLeftNav, setShowLeftNav] = useState(false);

    // States to manage selected profile
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [selectedCosts, setSelectedCosts] = useState(null);
    const [selectedPalletCosts, setSelectedPalletCosts] = useState(null);

    // State for account lists
    const [customerList, setCustomers] = useState([]);
    const [deliveries, setDeliveries] = useState([]);
    const [addressBook, setAddressBook] = useState([]);

    const [warehouseCosts, setWarehouseCosts] = useState([]);
    const [palletNetworkCosts, setPalletNetworkCosts] = useState([]);

    const [isModalOpen, setIsModalOpen] = useState(false);


    // ==========================================================================================================================
    // ========================================================[Functions]=======================================================
    // ==========================================================================================================================

    // DEBUG ====================================================================================================================

    // ==========================================================================================================================

    // User edit modal ===========================================================================================================
    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    // Function to update account data ============================================================================================
    const handleUpdate = async (updatedData) => {
        setIsLoading(true);

        // Handle the updated data, e.g., send it to the backend
        const result = await update_customer(updatedData);

        if (result.status === 200) {

            // Try to add/update the warehouse costs if they exist
            const costsToUpdate = {
                customer_id: updatedData.id, // Ensure the customer_id is set correctly
                weekly_pp: updatedData.weekly_pp || 0,
                handling: updatedData.handling || 0,
                use_rhd: Boolean(updatedData.use_rhd),
                rhd: updatedData.rhd || 0,
                wrap: updatedData.wrap || 0,
                band: updatedData.band || 0,
                pick: updatedData.pick || 0,
                supply_pallet: updatedData.supply_pallet || 0,
                twenty_ft_handball: updatedData.twenty_ft_handball || 0,
                forty_ft_handball: updatedData.forty_ft_handball || 0,
            }

            if (updatedData.has_storage && (selectedCosts && selectedCosts.length > 0)) {
                // Already has costs set so update them
                const result = await edit_warehouse_costs(costsToUpdate);
                if (result.status !== 200) {
                    setIsLoading(false);
                    showAlert({
                        message: 'Failed to update warehouse costs',
                        type: 'danger',
                        buttons: [{ label: 'Retry', type: 'danger' }],
                    });
                    return false;
                }
                
            } else if (updatedData.has_storage && (!selectedCosts || selectedCosts.length === 0)) { // No costs set so add them
                // Add new costs for the customer
                const result = await add_warehouse_costs(costsToUpdate);
                if (result.status !== 200) {
                    setIsLoading(false);
                    showAlert({
                        message: 'Failed to add warehouse costs',
                        type: 'danger',
                        buttons: [{ label: 'Retry', type: 'danger' }],
                    });
                    return false;
                }

            } else if (!updatedData.has_storage && (selectedCosts && selectedCosts.length > 0)) {
                // Remove costs for the customer
                const result = await delete_warehouse_costs(updatedData.id);
                if (result.status !== 200) {
                    setIsLoading(false);
                    showAlert({
                        message: 'Failed to delete warehouse costs',
                        type: 'danger',
                        buttons: [{ label: 'Retry', type: 'danger' }],
                    });
                    return false;
                }
            }

            if (updatedData.has_pallet_network) {
                const palletResult = await save_pallet_network_costs({
                    ...updatedData,
                    customer_id: updatedData.id,
                });
                if (palletResult.status !== 200) {
                    setIsLoading(false);
                    showAlert({
                        message: palletResult.data?.error || 'Failed to update pallet network prices',
                        type: 'danger',
                        buttons: [{ label: 'Close', type: 'danger' }],
                    });
                    return false;
                }
            } else if (selectedPalletCosts) {
                const palletResult = await delete_pallet_network_costs(updatedData.id);
                if (palletResult.status !== 200) {
                    setIsLoading(false);
                    showAlert({
                        message: palletResult.data?.error || 'Failed to remove pallet network prices',
                        type: 'danger',
                        buttons: [{ label: 'Close', type: 'danger' }],
                    });
                    return false;
                }
            }

            setIsLoading(false);
            showAlert({
                message: `${result.data.company} updated`,
                type: 'success',
                buttons: [{
                    label: 'Close',
                    onClick: () => {
                        setSelectedAccount(null);
                        setSelectedCosts(null);
                        setSelectedPalletCosts(null);
                        setActiveTab('customers');
                        fetchProfiles(); // refresh the list
                        fetchWarehouseCosts(); // refresh the warehouse costs
                        fetchPalletNetworkCosts();
                    }
                }],
            });
            return true;

        } else {
            setIsLoading(false);
            showAlert({
                message: 'Failed to update account',
                type: 'danger',
                buttons: [{ label: 'Retry', type: 'danger' }],
            });
            return false;
        }
    };


    // Fetch data from backend ============================================================================================
    const fetchProfiles = async () => {
        setIsLoading(true);
        // check for valid session to stop a loop on component mount
        if (!validSession()) {
            setIsLoading(false);
            showAlert({
                message: 'Session expired, please login again',
                type: 'danger',
                buttons: [{
                    label: 'Login',
                    type: 'danger',
                    onClick: () => {
                        logout();
                        navigate('/')
                        logger.log("Session expired, failed to fetch customers");
                    }
                }],
            });
            return;
        };

        const result = await fetch_all_customers();

        if (result.status === 200) {
            const customers = result.data.data || [];
            const sortedById = customers.sort((a, b) => a.id - b.id); // sort the list by id
            const sortedCustomers = sortList(sortedById, 'company');

            // create a set so only unique profiles are displayed
            const uniqueCustomersMap = new Map();

            // Iterate through the sorted customers
            sortedCustomers.forEach(profile => {
                if (!uniqueCustomersMap.has(profile.company)) {
                    uniqueCustomersMap.set(profile.company, profile);  // Add only the first profile with that company
                }
            });
            
            // Convert Map to an array of unique customer objects
            const uniqueCustomers = Array.from(uniqueCustomersMap.values());
            
            // Set the unique customers in state
            setCustomers(uniqueCustomers);

            setIsLoading(false);

        } else {
            setIsLoading(false);
            showAlert({
                message: 'Something went wrong, please try again',
                type: 'danger',
                buttons: [{
                    label: 'Login',
                    type: 'danger',
                    onClick: () => {
                        logout();
                        navigate('/')
                        logger.log("Session expired, please login again");
                    }
                }],
            });
        }
    };

    const fetchWarehouseCosts = async () => {
        setIsLoading(true);
        const result = await fetch_all_warehouse_costs();
        if (result.status === 200) {
            setWarehouseCosts(result.data.data || []);
            setIsLoading(false);
        } else {
            setWarehouseCosts([]);
            setIsLoading(false);
        };
    };

    const fetchPalletNetworkCosts = async () => {
        const result = await fetch_pallet_network_costs();
        setPalletNetworkCosts(result.status === 200 ? result.data.data || [] : []);
    };

    const fetchDeliveries = async (customerId) => {
        const result = await fetch_customer_deliveries(customerId);
        if (result.status === 200) {
            const sorted = (result.data.data || []).sort((a, b) => (a.route_name ?? '').localeCompare(b.route_name ?? ''));
            logger.log(`Fetched data 1: ${JSON.stringify(sorted[0])}`);
            setDeliveries(sorted);
        } else {
            setDeliveries([]);
        }
    };

    const fetchAddresses = async () => {
        const result = await fetch_address_book();
        if (result.status === 200) setAddressBook(result.data?.data || []);
    };

    const saveAddress = async (address) => {
        setIsLoading(true);
        const result = address.id ? await update_address_book_entry(address) : await add_address_book_entry(address);
        setIsLoading(false);
        if (result.status === 200) {
            await fetchAddresses();
            return true;
        }
        showAlert({ message: result.data?.error || 'Failed to save address', type: 'danger', buttons: [{ label: 'Close', type: 'danger' }] });
        return false;
    };

    const deleteAddress = (address) => {
        showAlert({
            message: `Delete ${address.friendly_name} from the Address Book? Existing jobs will retain their address snapshots.`,
            type: 'danger',
            buttons: [
                { label: 'Delete', type: 'danger', onClick: async () => {
                    const result = await delete_address_book_entry(address.id);
                    if (result.status === 200) fetchAddresses();
                    else showAlert({ message: result.data?.error || 'Failed to delete address', type: 'danger', buttons: [{ label: 'Close' }] });
                } },
                { label: 'Cancel', type: 'primary' },
            ],
        });
    };

    // fetch data from backend on component mount
    useEffect(() => {
        if (features?.routing === false && features?.warehouse === false) {
            navigate('/');
            return;
        }
        fetchProfiles();
        fetchWarehouseCosts();
        fetchPalletNetworkCosts();
        fetchAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Function to add new route ========================================================================================================
    const [openAddRouteModal, setOpenAddRouteModal] = useState(false);

    const handleAddRoute = () => {
        
        // open the add route modal
        setOpenAddRouteModal(true);
    };

    const submitRoute = async (data) => {
        setIsLoading(true);
        const result = await add_customer_delivery(data);
        setIsLoading(false);
        if (result.status === 200) {
            showAlert({
                message: `Delivery point added`,
                type: 'success',
                buttons: [{ label: 'Close', onClick: () => fetchDeliveries(selectedAccount.id) }],
            });
        } else {
            showAlert({
                message: result.data?.error || 'Failed to add delivery point',
                type: 'danger',
                buttons: [{ label: 'Retry', type: 'danger' }],
            });
        }
    };

    // Function to edit new route ========================================================================================================
    const [selectedRoute, setSelectedRoute] = useState(null);

    const handleEditRoute = (route) => {
        // open the add route modal and set the selected route
        setSelectedRoute(route);
        setOpenAddRouteModal(true);
    };

    const submitEditedRoute = async (data) => {
        setIsLoading(true);
        const result = await edit_customer_delivery(data);
        setIsLoading(false);
        if (result.status === 200) {
            showAlert({
                message: `Delivery point updated`,
                type: 'success',
                buttons: [{ label: 'Close', onClick: () => fetchDeliveries(selectedAccount.id) }],
            });
        } else {
            showAlert({
                message: result.data?.error || 'Failed to update delivery point',
                type: 'danger',
                buttons: [{ label: 'Retry', type: 'danger' }],
            });
        }
        setSelectedRoute(null);
    };

    // Function to delete route ========================================================================================================
    const handleConfirmDelete = (route) => {
        showAlert({
            message: `Are you sure you want to delete ${route.company} route?`,
            type: 'danger',
            buttons: [
                {
                    label: 'Yes',
                    type: 'danger',
                    onClick: () => handleDeleteRoute(route)
                },
                {
                    label: 'No',
                    type: 'primary',
                    onClick: () => {
                        setSelectedRoute(null);
                    }
                }
            ]
        });
    };

    // Function to delete delivery point ========================================================================================================
    const handleDeleteRoute = async (data) => {
        setIsLoading(true);
        const result = await delete_customer_delivery(data);
        setIsLoading(false);
        if (result.status === 200) {
            showAlert({
                message: `Delivery point deleted`,
                type: 'success',
                buttons: [{ label: 'Close', onClick: () => fetchDeliveries(selectedAccount.id) }],
            });
        } else {
            showAlert({
                message: result.data?.error || 'Failed to delete delivery point',
                type: 'danger',
                buttons: [{ label: 'Retry', type: 'danger' }],
            });
        }
        setSelectedRoute(null);
    };

    // Functions to create account ============================================================================================
    const {register, handleSubmit, reset, formState:{errors}} = useForm();

    const submitForm = async (data) => {
        setIsLoading(true);

        // Handle the updated data, e.g., send it to the backend
        const result = await add_new_customer(data);

        if (result.status === 200) {
            setIsLoading(false);
            showAlert({
                message: `${data.company} added`,
                type: 'success',
                buttons: [{
                    label: 'Close',
                    onClick: () => {
                        setSelectedAccount(null);
                        setActiveTab('customers');
                        fetchProfiles(); // refresh the list
                    }
                }],
            });

        } else {
            setIsLoading(false);
            showAlert({
                message: 'Failed to add new customer',
                type: 'danger',
                buttons: [{ label: 'Retry', type: 'danger' }],
            });
        }

        // Reset the form
        reset();
    };

    // ==========================================================================================================================

    const handleOpenEditModel = (customer) => {
        const costsForCustomer = warehouseCosts?.filter(c => c.customer_id === customer.id) ?? [];
        setSelectedCosts(costsForCustomer);
        setSelectedPalletCosts(palletNetworkCosts.find(cost => cost.customer_id === customer.id) || null);
        setSelectedAccount(customer);
        setDeliveries([]);
        fetchDeliveries(customer.id);
    };

    const [customerSearch, setCustomerSearch] = useState('');

    // RESTRICT ACCESS ====================================================================================================
    if (!isAdmin && !isDev && !permissions.perm_customers_view) {
        return (
            <div style={{ textAlign: 'center', marginTop: '30vh', height: '100vh', color: 'red' }}>
                <h3>Access Denied</h3>
                <p>You do not have permission to view this page</p>
            </div>
        )
    }

    return (
        <div id={theme} className="container-fluid closed-drivers-container">

            <Row>
                {/* Left Navigation Panel =================================================================================*/}
                <Col className={showLeftNav ? "left-nav" : "closed-nav"} style={{ height: '100vh', paddingBottom: '150px' }} onMouseEnter={() => setShowLeftNav(true)} onMouseLeave={() => setShowLeftNav(false)}>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, width: '100%' }}>
                        <li title="All Customers" className={`nav-item ${activeTab === 'customers' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'customers' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => {setActiveTab('customers'); setSelectedAccount(null);} : null}><FontAwesomeIcon icon={faBuilding} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">All Customers</span></li>
                        <li title="Address Book" className={`nav-item ${activeTab === 'addressBook' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'addressBook' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => {setActiveTab('addressBook'); setSelectedAccount(null);} : null}><FontAwesomeIcon icon={faAddressBook} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Address Book</span></li>
                        <li title="Add New Customer" className={`nav-item ${activeTab === 'addAccount' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : canEdit ? 'pointer' : 'default', background: activeTab === 'addAccount' ? 'rgba(25, 151, 235, 0.8)' : 'transparent', opacity: canEdit ? 1 : 0.4 }} onClick={!isLoading && canEdit ? () => {setActiveTab('addAccount'); setSelectedAccount(null);} : null}><FontAwesomeIcon icon={faPlus} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Add New Customer</span></li>
                    </ul>
                    {showLeftNav && (<>
                    {/* Drivers List */}
                    {activeTab === 'customers' && (
                        <div className="drivers-list" style={{ marginTop: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between' }}>
                                <h5><strong>Customer Accounts</strong></h5>
                                <FontAwesomeIcon icon={faDownload} style={{ cursor: 'pointer', width: 20 }} onClick={() => {downloadCSV(customerList)}} />
                            </div>

                            <input
                                type="text"
                                placeholder="Search customers..."
                                value={customerSearch}
                                onChange={(e) => setCustomerSearch(e.target.value)}
                                style={{ width: '100%', padding: '6px 10px', marginBottom: '8px', borderRadius: 4, border: '1px solid #555', background: 'transparent', color: 'inherit', fontSize: '0.9em' }}
                            />

                            <ul style={{ listStyle: 'none', padding: 0 }}>
                                {customerList
                                    .filter(c => c.company?.toLowerCase().includes(customerSearch.toLowerCase()))
                                    .map((customer) => (
                                    <li
                                        key={customer.company}
                                        style={{ cursor: isLoading ? 'not-allowed' : 'pointer', width: '100%', padding: '10px', borderRadius: 5, border: selectedAccount?.company === customer.company ? '1px solid #0588f3' : 'transparent' }}
                                        onClick={!isLoading ? () => {
                                            handleOpenEditModel(customer);
                                        } : null}
                                    >
                                        {customer.company}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    </>)}
                </Col>

                {/* Right Content Panel ============================================================================================*/}
                <Col className="closed-nav-content" style={{ padding: '20px' }}>

                {isLoading ? (
                            <div style={{ alignSelf: 'center', justifyContent: 'center', marginTop: '30vh' }}>
                                <ActivityIndicator />
                            </div>
                    ) : (
                    <>

                    {/* Edit Company Data Modal */}
                    {isModalOpen && (
                        <CustomerEditModal
                            theme={theme}
                            customer={selectedAccount}
                            selectedCosts={selectedCosts}
                            selectedPalletCosts={selectedPalletCosts}
                            isOpen={isModalOpen}
                            onClose={handleCloseModal}
                            onSubmit={handleUpdate}
                        />                        
                    )}

                    {/* Add Route Modal */}
                    {openAddRouteModal && (
                        <CustomerAddRouteModal
                            theme={theme}
                            customerList={customerList}
                            addressBook={addressBook}
                            edit={selectedRoute ? true : false}
                            customer={selectedRoute ?? selectedAccount}
                            isOpen={openAddRouteModal}
                            onClose={() => [setOpenAddRouteModal(false), setSelectedRoute(null)]}
                            onSubmit={selectedRoute ? submitEditedRoute : submitRoute}
                        />                        
                    )}


                    <div className="accounts-conatiner">
                        {activeTab === 'addressBook' && (
                            <AddressBookPanel addresses={addressBook} canEdit={canEdit} onSave={saveAddress} onDelete={deleteAddress} />
                        )}
                        {/* Right Content Drivers */}
                        {activeTab !== 'addAccount' && activeTab !== 'addressBook' && (
                            <>

                            {/* Header */}
                            <span style={{ textAlign: 'center', margin: '10px' }}>
                            {(() => {
                                switch (activeTab) {
                                    case 'customers':
                                        return <h3>Customer Information</h3>;
                                    default:
                                        return null; // Handle unexpected cases
                                }
                            })()}

                            {/* edit account buttons */}
                            {selectedAccount && canEdit && (
                                <div className="accounts-buttons">
                                    <span className="type-button" onClick={() => handleOpenModal()}><img src={edit} width={110} height={110} alt="edit-img" same-site="true"></img>Edit</span>
                                    <span className="type-button" onClick={() => handleAddRoute(selectedAccount.company)}><img src={route} width={110} height={110} alt="suspend-img" same-site="true"></img>Add Route</span>
                                </div> 
                            )}

                            {/* Account Details */}
                            </span>
                                {selectedAccount ? (
                                    <div style={{ margin: '10px'}}>
                                        <h5><strong>Customer Details</strong></h5>
                                        <p><strong>Company:</strong> {selectedAccount.company}</p>
                                        <p><strong>Address:</strong> {selectedAccount.address} {selectedAccount.postcode}</p>
                                        <p><strong>Email:</strong> {selectedAccount.email}</p>
                                        <p><strong>Phone:</strong> {selectedAccount.phone}</p>
                                        <p><strong>Account Ref:</strong> {selectedAccount.account_ref}</p>
                                        {/* {routeList.map((route, index) => (
                                            route.company === selectedAccount.company && (
                                                <>
                                                    <div
                                                        key={index}
                                                        style={{
                                                            key: route.id,
                                                            display: 'flex',
                                                            flexDirection: 'row',
                                                            marginTop: '20px',
                                                            justifyContent: 'space-between', // Align items to the left
                                                            alignItems: 'center', // Ensures items align at the top
                                                            borderTop: theme === 'dark' ? '1px dotted #fff' : '1px dotted #000',
                                                            paddingTop: '20px',
                                                            width: '100%',  // Make sure the container spans the width of its parent
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                                                            <span style={{ width: '10px', marginLeft: '0px', cursor: 'pointer' }} onClick={() => handleConfirmDelete(route)}><FontAwesomeIcon style={{ width:'30px' }} icon={faTimes} size={'xl'} /></span>
                                                            <span style={{ width: '10px', marginLeft: '50px', cursor: 'pointer' }} onClick={() => handleEditRoute(route)}><FontAwesomeIcon style={{ width: '30px' }} icon={faFileEdit} size={'xl'} /></span>
                                                        </div>
                                                            <span style={{ marginLeft: '50px' }}><strong>Route:</strong> {route.route}</span>
                                                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-evenly' }}>
                                                            <span style={{ marginRight: '50px' }}><strong>Cost:</strong> £{route.price ?? ' -------'}</span>
                                                            <span style={{ marginRight: '50px' }}><strong>Invoice To:</strong> {route.invoice_to?? ' --------'}</span>
                                                        </div>
                                                    </div>
                                                </>
                                            ))
                                        )} */}
                                        {deliveries.length === 0 && (
                                            <p style={{ marginTop: '20px', opacity: 0.5 }}>No delivery points found for this customer.</p>
                                        )}
                                        {deliveries.map((delivery) => (
                                                <div
                                                    key={delivery.id}
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        marginTop: '20px',
                                                        borderTop: theme === 'dark' ? '1px dotted #fff' : '1px dotted #000',
                                                        paddingTop: '16px',
                                                        width: '100%',
                                                        gap: '6px',
                                                    }}
                                                >
                                                    {/* Header row: icons + route name */}
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                                        {canEdit && (
                                                            <>
                                                                <span style={{ cursor: 'pointer' }} onClick={() => handleConfirmDelete(delivery)}>
                                                                    <FontAwesomeIcon style={{ width: '20px' }} icon={faTimes} size="xl" />
                                                                </span>
                                                                <span style={{ cursor: 'pointer' }} onClick={() => handleEditRoute(delivery)}>
                                                                    <FontAwesomeIcon style={{ width: '20px' }} icon={faFileEdit} size="xl" />
                                                                </span>
                                                            </>
                                                        )}
                                                        <strong>{delivery.route_name || '— no route name —'}</strong>
                                                        {delivery.company_name && delivery.company_name !== selectedAccount.company && (
                                                            <span style={{ opacity: 0.6, fontSize: '0.9em' }}>({delivery.company_name})</span>
                                                        )}
                                                        <div style={{ marginLeft: 'auto', marginRight: '16px' }}>
                                                            {delivery.mon && <span style={{ backgroundColor: '#00a2ff', height: '24px', padding: '0 8px', borderRadius: '999px', color: '#ffffff', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>Mon</span>}
                                                            {delivery.tue && <span style={{ backgroundColor: '#00a2ff', height: '24px', padding: '0 8px', borderRadius: '999px', color: '#ffffff', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>Tue</span>}
                                                            {delivery.wed && <span style={{ backgroundColor: '#00a2ff', height: '24px', padding: '0 8px', borderRadius: '999px', color: '#ffffff', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>Wed</span>}
                                                            {delivery.thu && <span style={{ backgroundColor: '#00a2ff', height: '24px', padding: '0 8px', borderRadius: '999px', color: '#ffffff', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>Thu</span>}
                                                            {delivery.fri && <span style={{ backgroundColor: '#00a2ff', height: '24px', padding: '0 8px', borderRadius: '999px', color: '#ffffff', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>Fri</span>}
                                                            {delivery.sat && <span style={{ backgroundColor: '#ae00ff', height: '24px', padding: '0 8px', borderRadius: '999px', color: '#ffffff', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>Sat</span>}
                                                            {delivery.sun && <span style={{ backgroundColor: '#ae00ff', height: '24px', padding: '0 8px', borderRadius: '999px', color: '#ffffff', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>Sun</span>}
                                                        </div>
                                                    </div>
                                                    {/* Detail row */}
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', paddingLeft: canEdit ? '56px' : '0' }}>
                                                        <span><strong>Collection:</strong> {delivery.collection_address_details?.friendly_name || '—'}</span>
                                                        <span><strong>Delivery:</strong> {delivery.delivery_address_details?.friendly_name || delivery.company_name || delivery.address || '—'}</span>
                                                        <span><strong>Cost:</strong> {delivery.price ? `£${delivery.price}` : '—'}</span>
                                                        <span><strong>Invoice To:</strong> {delivery.invoice_to || 'Same as customer'}</span>
                                                    </div>
                                                    {delivery.delivery_instructions && (
                                                        <div style={{ paddingLeft: canEdit ? '56px' : '0', opacity: 0.75, fontSize: '0.9em' }}>
                                                            <strong>Instructions:</strong> {delivery.delivery_instructions}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}

                                    </div>
                                ) : (
                                    <span style={{ textAlign: 'center'}}>
                                        {(() => {
                                            switch (activeTab) {
                                                case 'customers':
                                                    return <p>Please select a customer account from the list.</p>;
                                                default:
                                                    return null; // Handle unexpected cases
                                            }
                                        })()}
                                    </span>
                                )}
                            </>
                        )}
                    </div>

                    {/* Right Content Add Account */}
                    {activeTab === 'addAccount' && (
                        <>
                            <span style={{ textAlign: 'center' }}>
                                <h3>Add new customer</h3>
                            </span>
                            
                            <>
                            <form onSubmit={handleSubmit(submitForm)}>
                                <Row>
                                    {/* Left Column */}
                                    <Col md={6}>

                                    {/* Company */}
                                    <Form.Group className="signup-form">
                                        <Form.Label>Company</Form.Label>
                                        <Form.Control
                                        type="text"
                                        placeholder="Enter Company Name"
                                        {...register('company', { required: true, maxLength: 40 })}
                                        />
                                        {errors.company && (
                                        <div className="signup-error-container">
                                            <p className="signup-errors"><small>Company Name is required, Max 40</small></p>
                                        </div>
                                        )}
                                    </Form.Group>

                                    {/* Address 1 */}
                                    <Form.Group className="signup-form">
                                        <Form.Label>Address 1</Form.Label>
                                        <Form.Control
                                        type="text"
                                        placeholder="Enter Address"
                                        {...register('address1', { required: true, maxLength: 30 })}
                                        />
                                        {errors.address1 && (
                                        <div className="signup-error-container">
                                            <p className="signup-errors"><small>Road name required, Max 30</small></p>
                                        </div>
                                        )}
                                    </Form.Group>

                                    {/* Address 2 (Optional) */}
                                    <Form.Group className="signup-form">
                                        <Form.Label>Address 2 (Optional)</Form.Label>
                                        <Form.Control
                                        type="text"
                                        placeholder="Enter Address (Optional)"
                                        {...register('address2')}
                                        />
                                    </Form.Group>

                                    {/* Address 3 / Area */}
                                    <Form.Group className="signup-form">
                                        <Form.Label>Area</Form.Label>
                                        <Form.Control
                                        type="text"
                                        placeholder="Enter City / County"
                                        {...register('address3', { required: true, maxLength: 30 })}
                                        />
                                        {errors.address3 && (
                                        <div className="signup-error-container">
                                            <p className="signup-errors"><small>Area is required, Max 30</small></p>
                                        </div>
                                        )}
                                    </Form.Group>

                                    </Col>

                                    {/* Right Column */}
                                    <Col md={6}>

                                    {/* Postcode */}
                                    <Form.Group className="signup-form">
                                        <Form.Label>Postcode</Form.Label>
                                        <Form.Control
                                        type="text"
                                        placeholder="Enter Postcode"
                                        {...register('postcode', { required: true, maxLength: 8 })}
                                        />
                                        {errors.postcode && (
                                        <div className="signup-error-container">
                                            <p className="signup-errors"><small>Postcode is required, Max 8</small></p>
                                        </div>
                                        )}
                                    </Form.Group>

                                    {/* Email */}
                                    <Form.Group className="signup-form">
                                        <Form.Label>Email</Form.Label>
                                        <Form.Control
                                        type="email"
                                        placeholder="Enter Email"
                                        {...register('email', { required: true, maxLength: 50 })}
                                        />
                                        {errors.email && (
                                        <div className="signup-error-container">
                                            <p className="signup-errors"><small>Email is required, Max 50</small></p>
                                        </div>
                                        )}
                                    </Form.Group>

                                    {/* Contact Number */}
                                    <Form.Group className="signup-form">
                                        <Form.Label>Contact Number</Form.Label>
                                        <Form.Control
                                        type="text"
                                        placeholder="Enter Contact Number"
                                        {...register('phone', { required: true, maxLength: 13 })}
                                        />
                                        {errors.phone && (
                                        <div className="signup-error-container">
                                            <p className="signup-errors"><small>Contact number is required, Max 13</small></p>
                                        </div>
                                        )}
                                    </Form.Group>

                                    {/* Account Ref */}
                                    <Form.Group className="signup-form">
                                        <Form.Label>Account Ref</Form.Label>
                                        <Form.Control
                                        type="text"
                                        placeholder="CUS001"
                                        {...register('ref', { required: true, maxLength: 8 })}
                                        />
                                        {errors.ref && (
                                        <div className="signup-error-container">
                                            <p className="signup-errors"><small>Account reference is required, Max 8</small></p>
                                        </div>
                                        )}
                                    </Form.Group>
                                    </Col>
                                </Row>

                                {/* Submit Button */}
                                <Form.Group>
                                    <div className="signup-button-container">
                                        <Button as="sub" variant="primary" style={{ width: 200 }} onClick={handleSubmit(submitForm)}>Submit</Button>
                                    </div>
                                </Form.Group>
                                </form>
                            </>
                        </>
                    )}
                    </>
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default CustomerPage;
